import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface Session {
  id: string;
  userAgent: string;
  ipAddress: string;
  createdAt: string;
  lastActiveAt: string;
}

export function useActiveSessions() {
  return useQuery<Session[]>({
    queryKey: ['sessions'],
    queryFn: async () => {
      const { data } = await api.get<{ sessions: Session[] } | Session[]>('/api/v1/auth/sessions');
      if (Array.isArray(data)) {
        return data;
      }
      return data.sessions ?? []
    },
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.delete(`/api/v1/auth/sessions/${sessionId}`),
    onMutate: async (sessionId) => {
      await queryClient.cancelQueries({ queryKey: ['sessions'] });
      const previous = queryClient.getQueryData<Session[]>(['sessions']);
      queryClient.setQueryData<Session[]>(['sessions'], (old) =>
        old?.filter((s) => s.id !== sessionId) ?? [],
      );
      return { previous };
    },
    onError: (_err, _sessionId, context) => {
      queryClient.setQueryData(['sessions'], context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/api/v1/auth/sessions'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}
